/**
 * Component type registry — single source of truth for tires, brake pads,
 * wipers, 12V battery (and any future physical wearable). Adding a new
 * component type is one entry here plus optional icon; the list/detail
 * views + edit dialog adapt automatically.
 *
 * Contract for each type:
 *   icon          → lucide-react component for cards + rows
 *   label         → display name
 *   positions     → optional [{value,label}] for the Position dropdown
 *   wearFields    → array of {key, label, type, unit, min, max, step}
 *                   used by the edit dialog to render inputs
 *   summarizeWear(wear) → {display, pct?} for the row/card preview
 *                   `display` is the short right-side value ("6/32\"", "70%")
 *                   `pct` (0–100) drives the progress bar; omit for
 *                   qualitative types (wipers)
 *   healthTone(wear) → "green" | "amber" | "red" — colour ramp
 */
import { Disc3, Circle, Wind, Battery, Wrench } from "lucide-react";

// ---------- helpers ----------
const clampPct = (n) => Math.max(0, Math.min(100, n));
const numOr = (v, d = null) => (typeof v === "number" && Number.isFinite(v) ? v : d);

const TONE_HEALTHY = "healthy";
const TONE_WARN = "warn";
const TONE_BAD = "bad";

export const TONE_COLORS = {
  [TONE_HEALTHY]: "#34D17A",
  [TONE_WARN]: "#FFB340",
  [TONE_BAD]: "#FF3B30",
};

// ---------- type definitions ----------
export const COMPONENT_TYPES = {
  tire: {
    icon: Disc3,
    label: "Tires",
    wearLabel: "Tread Depth",
    lifetimeKm: 60000,
    positions: [
      { value: "front_pair", label: "Front Pair" },
      { value: "rear_pair", label: "Rear Pair" },
      { value: "front_left", label: "Front Left" },
      { value: "front_right", label: "Front Right" },
      { value: "rear_left", label: "Rear Left" },
      { value: "rear_right", label: "Rear Right" },
      { value: "set_of_4", label: "Set of 4" },
      { value: "spare", label: "Spare" },
    ],
    wearFields: [
      { key: "current_tread_32nds", label: "Current Tread (/32\")", type: "number", step: 0.5, min: 0, max: 20 },
      { key: "new_tread_32nds", label: "New Tread (/32\")", type: "number", step: 0.5, min: 0, max: 20, defaultValue: 10 },
      { key: "min_tread_32nds", label: "Minimum Tread (/32\")", type: "number", step: 0.5, min: 0, max: 20, defaultValue: 2 },
    ],
    summarizeWear(wear = {}) {
      const cur = numOr(wear.current_tread_32nds);
      const fresh = numOr(wear.new_tread_32nds, 10);
      const min = numOr(wear.min_tread_32nds, 2);
      if (cur == null) return { display: "—" };
      const range = Math.max(0.1, fresh - min);
      const pct = clampPct(((cur - min) / range) * 100);
      return { display: `${cur}/32"`, pct };
    },
    healthTone(wear = {}) {
      const cur = numOr(wear.current_tread_32nds);
      const min = numOr(wear.min_tread_32nds, 2);
      if (cur == null) return TONE_HEALTHY;
      if (cur <= min) return TONE_BAD;
      if (cur <= min + 2) return TONE_WARN;
      return TONE_HEALTHY;
    },
  },
  brake_pads: {
    icon: Circle,
    label: "Brake Pads",
    wearLabel: "Pad Thickness",
    lifetimeKm: 40000,
    positions: [
      { value: "front_axle", label: "Front Axle" },
      { value: "rear_axle", label: "Rear Axle" },
      { value: "all_four", label: "All Four" },
    ],
    wearFields: [
      { key: "current_thickness_mm", label: "Current Thickness (mm)", type: "number", step: 0.5, min: 0, max: 20 },
      { key: "new_thickness_mm", label: "New Thickness (mm)", type: "number", step: 0.5, min: 0, max: 20, defaultValue: 10 },
      { key: "min_thickness_mm", label: "Minimum Thickness (mm)", type: "number", step: 0.5, min: 0, max: 20, defaultValue: 3 },
    ],
    summarizeWear(wear = {}) {
      const cur = numOr(wear.current_thickness_mm);
      const fresh = numOr(wear.new_thickness_mm, 10);
      const min = numOr(wear.min_thickness_mm, 3);
      if (cur == null) return { display: "—" };
      const range = Math.max(0.1, fresh - min);
      const pct = clampPct(((cur - min) / range) * 100);
      return { display: `${cur} mm`, pct };
    },
    healthTone(wear = {}) {
      const cur = numOr(wear.current_thickness_mm);
      const min = numOr(wear.min_thickness_mm, 3);
      if (cur == null) return TONE_HEALTHY;
      if (cur <= min) return TONE_BAD;
      if (cur <= min + 2) return TONE_WARN;
      return TONE_HEALTHY;
    },
  },
  wipers: {
    icon: Wind,
    label: "Wiper Blades",
    wearLabel: "Condition",
    lifetimeDays: 365,
    positions: [
      { value: "front_pair", label: "Front Pair" },
      { value: "rear", label: "Rear" },
      { value: "driver", label: "Driver Only" },
      { value: "passenger", label: "Passenger Only" },
    ],
    wearFields: [
      {
        key: "condition",
        label: "Condition",
        type: "select",
        options: [
          { value: "excellent", label: "Excellent" },
          { value: "good", label: "Good" },
          { value: "fair", label: "Fair — Replace Soon" },
          { value: "worn", label: "Worn — Replace Now" },
        ],
      },
    ],
    summarizeWear(wear = {}) {
      const map = {
        excellent: { display: "Excellent", pct: 100 },
        good: { display: "Good", pct: 75 },
        fair: { display: "Fair", pct: 40 },
        worn: { display: "Worn", pct: 10 },
      };
      return map[wear.condition] || { display: "—" };
    },
    healthTone(wear = {}) {
      if (wear.condition === "worn") return TONE_BAD;
      if (wear.condition === "fair") return TONE_WARN;
      return TONE_HEALTHY;
    },
  },
  battery: {
    icon: Battery,
    label: "12V Battery",
    wearLabel: "Health",
    lifetimeDays: 730,
    positions: [{ value: "main", label: "Main" }, { value: "aux", label: "Auxiliary" }],
    wearFields: [
      { key: "health_pct", label: "Battery Health (%)", type: "number", step: 1, min: 0, max: 100, defaultValue: 100 },
    ],
    summarizeWear(wear = {}) {
      const h = numOr(wear.health_pct);
      if (h == null) return { display: "—" };
      const label = h >= 85 ? "Excellent" : h >= 65 ? "Good" : h >= 40 ? "Fair" : "Poor";
      return { display: label, pct: clampPct(h), secondary: `${h}%` };
    },
    healthTone(wear = {}) {
      const h = numOr(wear.health_pct);
      if (h == null) return TONE_HEALTHY;
      if (h < 40) return TONE_BAD;
      if (h < 65) return TONE_WARN;
      return TONE_HEALTHY;
    },
  },
};

/**
 * Human-readable "usage" line for a component row — mirrors the layout in
 * the reference mock: "16,300 km driven" · "~9,700 km left".
 *
 * We pick the axis (km vs days) based on which axis the component's
 * lifetime is expressed in. Tires/brakes use km (distance owns the wear),
 * wipers/battery use days (time owns the wear). The estimate is a rough
 * projection anchored to `lifetimeKm`/`lifetimeDays` OR — when we have
 * enough wear signal — extrapolated from the current wear pct.
 */
export function usageBreakdown(component, { distanceDrivenKm } = {}) {
  const meta = COMPONENT_TYPES[component.type] || {};
  const wear = component.wear || {};
  const sum = meta.summarizeWear ? meta.summarizeWear(wear) : {};

  if (meta.lifetimeKm) {
    if (typeof distanceDrivenKm !== "number") {
      return { driven: "Not installed", remaining: "—" };
    }
    // Prefer wear-pct-based projection when the user has actually recorded a
    // current-wear number; otherwise fall back to the type's nominal lifetime.
    let remaining;
    if (typeof sum.pct === "number" && distanceDrivenKm > 0 && sum.pct < 100) {
      const used = 100 - sum.pct;
      remaining = Math.round((distanceDrivenKm / used) * sum.pct);
    } else {
      remaining = Math.max(0, Math.round(meta.lifetimeKm - distanceDrivenKm));
    }
    return {
      driven: `${Math.round(distanceDrivenKm).toLocaleString()} km driven`,
      remaining: `~${remaining.toLocaleString()} km left`,
    };
  }

  if (meta.lifetimeDays) {
    const daysIn = daysSince(component.install_date);
    if (daysIn == null) return { driven: "Not installed", remaining: "—" };
    let remaining;
    if (typeof sum.pct === "number" && daysIn > 0 && sum.pct < 100) {
      const used = 100 - sum.pct;
      remaining = Math.round((daysIn / used) * sum.pct);
    } else {
      remaining = Math.max(0, meta.lifetimeDays - daysIn);
    }
    return {
      driven: `${daysIn.toLocaleString()} days installed`,
      remaining: `~${remaining.toLocaleString()} days left`,
    };
  }
  return { driven: "", remaining: "" };
}

function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  return Math.max(0, Math.floor((now - d) / (1000 * 60 * 60 * 24)));
}

export const COMPONENT_TYPE_LIST = Object.entries(COMPONENT_TYPES).map(([key, cfg]) => ({
  key,
  ...cfg,
}));

export function componentTypeMeta(type) {
  return COMPONENT_TYPES[type] || { icon: Wrench, label: type, wearFields: [], summarizeWear: () => ({ display: "—" }), healthTone: () => TONE_HEALTHY };
}

// Status pill palette — mirrors the maintenance module's statusMeta so the
// visual language stays consistent across both tabs.
export const COMPONENT_STATUS_META = {
  active: { label: "Active", color: "#34D17A", ring: "rgba(52,209,122,0.18)" },
  stored: { label: "Stored", color: "#FFB340", ring: "rgba(255,179,64,0.20)" },
  archived: { label: "Archived", color: "#8A8A8E", ring: "rgba(138,138,142,0.18)" },
};
